import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { SegmentButton } from '@/components/ui/segment-button';

import { addDays, endOfDayMs, shortDate } from './format';

type Preset = 14 | 30 | 'custom';

interface ExtendTrialDialogProps {
  onClose: () => void;
  name: string;
  currentEndMs: number;
  onExtend: (trialEnd: string) => Promise<unknown>;
}

export function ExtendTrialDialog({
  onClose,
  name,
  currentEndMs,
  onExtend,
}: ExtendTrialDialogProps) {
  const [preset, setPreset] = useState<Preset>(14);
  const [customDate, setCustomDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();

  const newEnd =
    preset === 'custom' ? customDate : addDays(currentEndMs, preset);

  const handleExtend = async () => {
    setIsSaving(true);
    try {
      await onExtend(newEnd);
      toast.success(
        'Trial extended',
        `${name} now has the trial until ${shortDate(endOfDayMs(newEnd))}.`
      );
      onClose();
    } catch (e) {
      toast.error(
        'Could not extend the trial',
        e instanceof Error ? e.message : 'Unknown error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Extend trial</DialogTitle>
          <DialogDescription>
            {name} keeps the trial until the new date.
          </DialogDescription>
        </DialogHeader>
        <SegmentButton<Preset>
          options={[
            { value: 14, label: '+14 days' },
            { value: 30, label: '+30 days' },
            { value: 'custom', label: 'Custom date' },
          ]}
          selected={preset}
          onSelect={setPreset}
        />
        {preset === 'custom' && (
          <Input
            type="date"
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
          />
        )}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-md border p-3">
            <p className="text-xs text-muted-foreground">Ends now</p>
            <p className="mt-1 font-semibold">{shortDate(currentEndMs)}</p>
          </div>
          <div className="rounded-md border p-3">
            <p className="text-xs text-muted-foreground">New end</p>
            <p className="mt-1 font-semibold">
              {newEnd ? shortDate(endOfDayMs(newEnd)) : 'Pick a date'}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleExtend} disabled={!newEnd || isSaving}>
            {isSaving ? 'Extending...' : 'Extend trial'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
